/// <amd-module name="Test/Analytics/Widgets/DetailsWidget/DetailsWidgetTreeProvider" />
import { DetailsWidgetColumnNames } from "Test/Analytics/Widgets/DetailsWidget/Constants";
import { IDetailsWidgetItem } from "Test/Analytics/Widgets/DetailsWidget/Contracts";
import { DetailsWidgetSource } from "Test/Analytics/Widgets/DetailsWidget/DetailsWidgetSource";
import * as Resources from "Test/Analytics/Widgets/DetailsWidget/Resources";
import { IFilterData } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { ColumnSorting, SortOrder } from "VSSUI/Table";
import { ITreeColumn, renderExpandableTreeCell, renderTreeCell } from "VSSUI/TreeEx";
import { ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import * as TelemetryService from "Test/ManualTestReports/TestReportsCommon/TelemetryService";
import * as TelemetryConstants from "Test/ManualTestReports/TestReportsCommon/TelemetryConstants";

export class DetailsWidgetTreeProvider extends TreeItemProvider<ObservableValue<IDetailsWidgetItem>> {
    private _sortProps: { columnIndex: number; sortOrder: SortOrder } | undefined;

    private _filterData: IFilterData | undefined;
    private _lastSelectedPlanId: number | undefined;
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        super([]);
        this._pageContext = pageContext;
        this._source = new DetailsWidgetSource("detailsWidgetSource", pageContext);
    }

    public getRootLevelItems(
        filterData?: IFilterData,
        sortProps?: { columnIndex: number; sortOrder: SortOrder },
        lastSelectedPlanId?: number
    ): Promise<any> {
        this._sortProps = sortProps;
        this._filterData = filterData;
        this._lastSelectedPlanId = lastSelectedPlanId;
        return this._source
            .getTestPlanSummaryDetails(filterData, this._getSortInputsForSource(sortProps), lastSelectedPlanId)
            .then((detailsWidgetItems: IDetailsWidgetItem[]) => {
                var treeItems = this._convertDetailsWidgetItemsToTreeItems(detailsWidgetItems, sortProps);

                this.clear();
                this.splice(undefined, undefined, [{ items: treeItems }]);
            });
    }

    private _sortNestedChildItems(
        treeItems: ITreeItem<ObservableValue<IDetailsWidgetItem>>[],
        sortProps: { columnIndex: number; sortOrder: SortOrder }
    ) {
        for (let i = 0; i < treeItems.length; i++) {
            if (treeItems[i].expanded) {
                let tempTreeChildItems = treeItems[i].childItems;
                if (tempTreeChildItems) {
                    this._sortItems(tempTreeChildItems, sortProps);
                    this._sortNestedChildItems(tempTreeChildItems, sortProps);
                }
            }
        }
    }

    public toggle(treeItem: ITreeItem<ObservableValue<IDetailsWidgetItem>>): void {
        super.toggle(treeItem);

        if (treeItem.expanded) {
            this._getChildItems(treeItem, this._sortProps);
        }
    }

    public get columns(): IObservableValue<ITreeColumn<ObservableValue<IDetailsWidgetItem>>>[] {
        return this._columns;
    }

    private _convertDetailsWidgetItemsToTreeItems(
        data: IDetailsWidgetItem[],
        sortProps?: { columnIndex: number; sortOrder: SortOrder }
    ): ITreeItem<ObservableValue<IDetailsWidgetItem>>[] {
        let treeItems: ITreeItem<ObservableValue<IDetailsWidgetItem>>[] = [];
        let childItemValue: ITreeItem<ObservableValue<IDetailsWidgetItem | undefined>> = {
            data: new ObservableValue(undefined)
        };
        data.forEach((widgetItem: IDetailsWidgetItem) => {
            const expanded = true;
            const treeItem = {
                childItems: [childItemValue],
                expanded: false,
                data: new ObservableValue<IDetailsWidgetItem>(widgetItem)
            } as ITreeItem<ObservableValue<IDetailsWidgetItem>>;

            treeItems.push(treeItem);
        });
        this._sortItems(treeItems, sortProps);

        return treeItems;
    }

    private _sortItems(items: ITreeItem<IObservableValue<IDetailsWidgetItem>>[], sortProps?: { columnIndex: number; sortOrder: SortOrder }) {
        if (!sortProps) {
            return items;
        } else {
            var diff;
            items.sort((item1: ITreeItem<ObservableValue<IDetailsWidgetItem>>, item2: ITreeItem<ObservableValue<IDetailsWidgetItem>>) => {
                switch (sortProps.columnIndex) {
                    case 1:
                    case 2:
                    case 3:
                    case 4:
                    case 5:
                        var columnName = this._getColumnIndexToColumnNameMap()[sortProps.columnIndex];
                        diff = (item1.data.value[columnName] as number) - (item2.data.value[columnName] as number);
                        break;
                    case 0:
                        diff = Utils_String.localeComparer(item1.data.value.containerName, item2.data.value.containerName);
                        break;

                    default:
                        diff = 0;
                        break;
                }
                return sortProps.sortOrder === SortOrder.ascending ? diff : -diff;
            });
        }
    }

    private _getChildItems(
        treeItem: ITreeItem<ObservableValue<IDetailsWidgetItem>>,
        sortProps?: { columnIndex: number; sortOrder: SortOrder }
    ): void {
        this._source
            .getTestSuiteChildren(
                treeItem.data.value.id,
                treeItem.data.value.depth,
                this._filterData,
                this._getSortInputsForSource(sortProps),
                this._lastSelectedPlanId
            )
            .then(
                (detailsWidgetItems: IDetailsWidgetItem[]) => {
                    var treeItems = this._convertDetailsWidgetItemsToTreeItems(detailsWidgetItems, undefined);
                    this.splice(treeItem, treeItem.childItems);
                    this.splice(treeItem, undefined, [{ items: treeItems }]);
                },
                (error: Error) => {
                    let properties: { [key: string]: string } = {};
                    properties["error"] = JSON.stringify(error);
                    TelemetryService.publishEvent(this._pageContext, TelemetryConstants.DetailsWidget_TreeExpansionError, properties);
                }
            );
    }

    private _getSortInputsForSource(sortProps?: {
        columnIndex: number;
        sortOrder: SortOrder;
    }): { columnName: string; sortOrder: SortOrder } | undefined {
        if (!sortProps) {
            return undefined;
        } else {
            return {
                columnName: this._getColumnIndexToColumnNameMap()[sortProps.columnIndex],
                sortOrder: sortProps.sortOrder
            };
        }
    }

    public get sorting(): ColumnSorting<IDetailsWidgetItem> {
        return this._columnSorting;
    }

    private _getColumnIndexToColumnNameMap(): { [key: number]: string } {
        return {
            0: DetailsWidgetColumnNames.itemName,
            1: DetailsWidgetColumnNames.pointCount,
            2: DetailsWidgetColumnNames.runPercentage,
            3: DetailsWidgetColumnNames.passedPercentage,
            4: DetailsWidgetColumnNames.failedPercentage,
            5: DetailsWidgetColumnNames.notRunCount
        };
    }

    private _columns = [
        new ObservableValue({
            id: DetailsWidgetColumnNames.itemName,
            name: Resources.TestPlanTitle,
            width: -100,
            minWidth: 400,
            renderCell: renderExpandableTreeCell,
            sortProps: {}
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>),
        new ObservableValue({
            id: DetailsWidgetColumnNames.pointCount,
            name: Resources.TestPoints,
            width: 130,
            renderCell: renderTreeCell,
            sortProps: {}
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>),
        new ObservableValue({
            id: DetailsWidgetColumnNames.runPercentage,
            name: Resources.RunPercentage,
            width: 130,
            renderCell: renderTreeCell,
            sortProps: {
                sortOrder: SortOrder.descending
            }
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>),
        new ObservableValue({
            id: DetailsWidgetColumnNames.passedPercentage,
            name: Resources.PassedPercentage,
            width: 130,
            renderCell: renderTreeCell,
            sortProps: {}
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>),
        new ObservableValue({
            id: DetailsWidgetColumnNames.failedPercentage,
            name: Resources.FailedTestPercentage,
            width: 130,
            renderCell: renderTreeCell,
            sortProps: {}
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>),
        new ObservableValue({
            id: DetailsWidgetColumnNames.notRunCount,
            name: Resources.NotRunCount,
            width: 130,
            renderCell: renderTreeCell,
            sortProps: {}
        } as ITreeColumn<ObservableValue<IDetailsWidgetItem>>)
    ];

    private _source: DetailsWidgetSource;
    public columnSortingDelegate = (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<any>) => {
        this.columns[columnIndex].value.sortProps = {
            sortOrder: proposedSortOrder
        };

        for (var i = 0; i < this._columns.length; i++) {
            if (i !== columnIndex) {
                this.columns[i].value.sortProps = {};
            }
        }

        let sortProps = { columnIndex: columnIndex, sortOrder: proposedSortOrder };
        let treeItems = this.roots.slice();
        let treeItemsExpanded: ITreeItem<ObservableValue<IDetailsWidgetItem>>[];
        treeItemsExpanded = this.roots.filter((x) => x.expanded === true);
        if (sortProps && treeItems && treeItemsExpanded && treeItems.length > 0 && treeItemsExpanded.length > 0 && sortProps.columnIndex >= 0) {
            this._sortItems(treeItems, sortProps);
            this._sortNestedChildItems(treeItems, sortProps);
            this.clear();
            this.splice(undefined, undefined, [{ items: treeItems }]);
        } else {
            this.getRootLevelItems(this._filterData, { columnIndex: columnIndex, sortOrder: proposedSortOrder }, this._lastSelectedPlanId);
        }
        TelemetryService.publishEvent(this._pageContext, TelemetryConstants.Feature_ProgressReport, {
            columnName: this.columns[columnIndex].value.id,
            columnSortOrder: proposedSortOrder == SortOrder.ascending ? "ascending" : "descending",
            event: TelemetryConstants.Event_DetailsWidgetColumnSort
        });
    };
    //Adding the Delegate 'columnSortingDelegate' for L0's
    private _columnSorting: ColumnSorting<IDetailsWidgetItem> = new ColumnSorting<IDetailsWidgetItem>(this.columnSortingDelegate);
}
